import React from 'react';
import { gql, useQuery } from '@apollo/client';

const SCAN_ALL_CONFLICTS = gql`
  query ScanAllConflicts($limit: Number) {
    scanAllConflicts(limit: $limit) {
      context1Id
      context1Name
      context1Version
      context2Id
      context2Name
      context2Version
      similarity
      hasConflict
      reason
      severity
    }
  }
`;

export function ConflictDetector() {
  const { data, loading, error, refetch } = useQuery(SCAN_ALL_CONFLICTS, {
    variables: { limit: 50 },
    fetchPolicy: 'network-only',
  });

  if (loading) {
    return (
      <div className="p-4">
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
          <span>Scanning for conflicts...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500">
        Error scanning conflicts: {error.message}
      </div>
    );
  }

  const conflicts = data?.scanAllConflicts || [];
  const actualConflicts = conflicts.filter((c: any) => c.hasConflict);
  const potentialConflicts = conflicts.filter((c: any) => !c.hasConflict && c.similarity > 0.7);

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Conflict Detection</h2>
        <button
          onClick={() => refetch()}
          className="bg-primary text-white px-4 py-2 rounded hover:bg-primary/90"
        >
          Rescan
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-3xl font-bold text-red-700">{actualConflicts.length}</div>
          <div className="text-sm text-red-600">Actual Conflicts</div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="text-3xl font-bold text-yellow-700">{potentialConflicts.length}</div>
          <div className="text-sm text-yellow-600">High Similarity</div>
        </div>
      </div>

      {actualConflicts.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-bold mb-3 text-red-700">⚠️ Detected Conflicts</h3>
          <div className="space-y-3">
            {actualConflicts.map((conflict: any, i: number) => (
              <div
                key={i}
                className={`border-l-4 p-4 rounded ${
                  conflict.severity === 'high' ? 'border-red-500 bg-red-50' :
                  conflict.severity === 'medium' ? 'border-orange-500 bg-orange-50' :
                  'border-yellow-500 bg-yellow-50'
                }`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <div className="font-semibold">
                      {conflict.context1Name} v{conflict.context1Version}
                      <span className="mx-2 text-gray-400">↔</span>
                      {conflict.context2Name} v{conflict.context2Version}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-1 rounded font-medium ${
                      conflict.severity === 'high' ? 'bg-red-200 text-red-800' :
                      conflict.severity === 'medium' ? 'bg-orange-200 text-orange-800' :
                      'bg-yellow-200 text-yellow-800'
                    }`}>
                      {conflict.severity.toUpperCase()}
                    </span>
                    <span className="text-xs text-gray-500">
                      {(conflict.similarity * 100).toFixed(1)}% similar
                    </span>
                  </div>
                </div>
                <p className="text-sm text-gray-700">{conflict.reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {potentialConflicts.length > 0 && (
        <div>
          <h3 className="text-lg font-bold mb-3 text-yellow-700">ℹ️ High Similarity (No Conflict Detected)</h3>
          <div className="space-y-2">
            {potentialConflicts.map((conflict: any, i: number) => (
              <div key={i} className="border border-gray-200 p-3 rounded bg-gray-50">
                <div className="flex justify-between items-center">
                  <div className="text-sm">
                    <span className="font-medium">{conflict.context1Name}</span>
                    <span className="mx-2 text-gray-400">↔</span>
                    <span className="font-medium">{conflict.context2Name}</span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {(conflict.similarity * 100).toFixed(1)}% similar
                  </span>
                </div>
                <p className="text-xs text-gray-600 mt-1">{conflict.reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {conflicts.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <div className="text-4xl mb-2">✓</div>
          <div className="text-lg">No conflicts detected</div>
          <p className="text-sm mt-2">All contexts are compatible</p>
        </div>
      )}
    </div>
  );
}
